import {
  type BuyerMaterialSpecificationAdminListItem,
  type BuyerMaterialSpecificationBuyerListItem,
  getMaterialComponentLabel,
  isObject,
  useApiTradeV1TradeCompaniesBuyerMaterialSpecificationsRetrieve,
} from 'api';
import { capitalizeString } from 'ui';

import { AutocompleteField, type AutocompleteFieldProps } from '../AutocompleteField/AutocompleteField';

type BuyerMaterialSpecificationsListResponse =
  | BuyerMaterialSpecificationAdminListItem
  | BuyerMaterialSpecificationBuyerListItem;

const getOptionLabel = (option: BuyerMaterialSpecificationsListResponse) => {
  if (typeof option === 'number') {
    // This may be a number (the ID) immediately after page load,
    // before the buyerMaterialSpec data is fetched. Once hydrated via the API,
    // it will be the full BuyerMaterialSpecificationsListResponse object.
    return String(`Buyer material specification #${option}`);
  }
  if (isObject(option) && option.material && option.material_form && option.material_grade) {
    const materialComponentsLabels = option.material_component_values
      .map((materialComponent) => getMaterialComponentLabel(materialComponent))
      .join(', ');
    return `${option.material.title} ${capitalizeString(option.material_form.type)} ${capitalizeString(option.material_colour?.name || '')} - ${option.material_grade.name} - ${materialComponentsLabels}`;
  }
  return '';
};

export type BuyerMaterialSpecificationsAutocompleteProps = Omit<
  AutocompleteFieldProps<BuyerMaterialSpecificationsListResponse>,
  'options' | 'label' | 'defaultValue' | 'onSelectOption'
> & {
  tradeCompanyId?: number;
  label?: string;
  defaultValue?: BuyerMaterialSpecificationsListResponse['id'];
  onSelectOption?: (value: BuyerMaterialSpecificationsListResponse) => void;
};

export const BuyerMaterialSpecificationsAutocomplete = ({
  tradeCompanyId,
  label = 'Buyer material specification',
  defaultValue,
  onSelectOption,
  ...props
}: BuyerMaterialSpecificationsAutocompleteProps) => {
  const { data, isLoading } = useApiTradeV1TradeCompaniesBuyerMaterialSpecificationsRetrieve(
    tradeCompanyId || 0,
    undefined,
    {
      query: {
        enabled: Boolean(tradeCompanyId),
      },
    }
  );

  return (
    <AutocompleteField<BuyerMaterialSpecificationsListResponse>
      options={data?.results || []}
      loading={isLoading}
      {...props}
      disabled={!tradeCompanyId}
      label={label}
      getOptionLabel={(option) => getOptionLabel(option)}
      valueField="id"
      data-testid="buyer-material-specifications-autocomplete"
      filterOptions={(options, state) =>
        options.filter((option) => getOptionLabel(option).toLowerCase().includes(state.inputValue.toLowerCase()))
      }
      onSelectOption={onSelectOption}
    />
  );
};
